// ABOUTME: State and keyboard handling for the header search launcher: rows, selection, opening.
// ABOUTME: Ranks dashboard links and widgets, offers Add link, and falls through to a web search.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DashboardItem } from "../services/dashboardService";
import { rankItems } from "../utils/commandSearch";
import { preferredEngine, SEARCH_ENGINES } from "../utils/searchEngines";
import { sanitizeUrl } from "../utils/urlValidation";
import { useUIStore } from "../stores/ui";
import { useDashboardStore } from "../stores/dashboard";

export type LauncherRow =
  | { kind: "item"; item: DashboardItem; href: string }
  | { kind: "search"; engine: string; href: string; query: string }
  | { kind: "action"; id: "add-link"; label: string };

/** True when the query plausibly asks for the add-link action. Matches an
 *  empty query (the launcher's resting state) or a prefix of "add link" /
 *  "new link", so "add", "add l", and "new" all find it. */
function matchesAddLink(query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return "add link".startsWith(q) || "new link".startsWith(q);
}

/** True when focus is somewhere the user is actually typing. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

/**
 * The rows the launcher offers for a query: matching links and widgets first,
 * then the Add link action when the query asks for it, then the web.
 */
export function launcherRows(items: DashboardItem[], query: string): LauncherRow[] {
  const trimmed = query.trim();
  const addLinkAction: LauncherRow = { kind: "action", id: "add-link", label: "Add link" };

  // An empty field offers the one thing most people focus it to do.
  if (!trimmed) return [addLinkAction];

  const matches = rankItems(items, trimmed).flatMap<LauncherRow>((item) => {
    if (item.type === "widget") {
      return [{ kind: "item", item, href: "" }];
    }
    const href = sanitizeUrl(item.url || "");
    return href ? [{ kind: "item", item, href }] : [];
  });

  const action = matchesAddLink(trimmed) ? [addLinkAction] : [];

  // The preferred engine is always offered; a second one joins it when
  // nothing on the dashboard matches.
  const engine = preferredEngine();
  const fallbacks: LauncherRow[] = [
    { kind: "search", engine: engine.name, href: engine.url(trimmed), query: trimmed },
  ];
  if (matches.length === 0) {
    const alt = SEARCH_ENGINES.find((e) => e.name !== engine.name);
    if (alt) {
      fallbacks.push({ kind: "search", engine: alt.name, href: alt.url(trimmed), query: trimmed });
    }
  }

  // Real links are the more likely intent, so the action ranks below them.
  return [...matches, ...action, ...fallbacks];
}

/**
 * Drives the header search field as a launcher. ⌘/Ctrl-K, or "/" when not
 * already typing, focuses the field; results show while it has focus.
 */
export function useLauncher(items: DashboardItem[]) {
  const [query, setQueryState] = useState("");
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openLinksInNewTab = useUIStore((s) => s.openLinksInNewTab);
  const openSheet = useDashboardStore((s) => s.openSheet);

  const rows = useMemo(() => launcherRows(items, query), [items, query]);
  const expanded = focused && rows.length > 0;
  // Items can change under an open query, so the selection is held in range.
  const activeIndex = Math.min(active, Math.max(rows.length - 1, 0));

  /** A new query starts its selection back at the top row. */
  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setActive(0);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      const isSlash = e.key === "/" && !e.metaKey && !e.ctrlKey && !isTypingTarget(e.target);
      if (isCmdK || isSlash) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  /** Empties the field and keeps it focused for a fresh query. */
  const clear = useCallback(() => {
    setQuery("");
    inputRef.current?.focus();
  }, [setQuery]);

  /** Empties the field and gives focus back to the page. */
  const dismiss = useCallback(() => {
    setQuery("");
    inputRef.current?.blur();
  }, [setQuery]);

  const openRow = useCallback(
    (row: LauncherRow, forceNewTab: boolean) => {
      if (row.kind === "action") {
        dismiss();
        openSheet("Links");
        return;
      }
      if (row.kind === "item" && row.item.type === "widget") {
        // Widgets live on the page; scroll to it rather than navigating.
        dismiss();
        document
          .querySelector(`[data-item-id="${row.item.id}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (!row.href) return;
      dismiss();
      if (forceNewTab || openLinksInNewTab) {
        window.open(row.href, "_blank", "noopener,noreferrer");
      } else {
        window.location.assign(row.href);
      }
    },
    [dismiss, openLinksInNewTab, openSheet]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      dismiss();
      return;
    }
    if (!rows.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((activeIndex + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((activeIndex - 1 + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[activeIndex];
      if (row) openRow(row, e.metaKey || e.ctrlKey);
    }
  };

  return {
    query,
    setQuery,
    clear,
    rows,
    active: activeIndex,
    setActive,
    expanded,
    inputRef,
    openRow,
    inputHandlers: {
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
      onKeyDown,
    },
  };
}
